import React, { useState, useEffect } from 'react';
import RuleGroup from './components/RuleGroup';
import Skeleton from 'react-loading-skeleton';
import shortid from 'shortid';
import { getRandomDecimal } from 'store/helpers/helperFunctions';

/** Finds a node by ID.**/
export const findNodeById = (id, node) => {
  if (node.id === id) {
    return node;
  }
  if (node.subConditionExpressions) {
    for (const rule of node.subConditionExpressions) {
      const found = findNodeById(id, rule);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/** Finds a node's parent node by ID.
 * @param {Number} id The node ID.
 * @param {Object} node The starting node.
 * @param {Object} parent The starting parent.
 * @returns {Object} The searched node's parent.
 */
export const findParentById = (id, node, parent) => {
  if (!parent) {
    parent = node;
  }
  if (node.id === id) {
    return parent;
  }
  if (node.subConditionExpressions) {
    parent = node;
    for (const rule of node.subConditionExpressions) {
      const found = findParentById(id, rule, parent);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/** Resets a query's node temporary IDs.
 * @param {string} mode "random" to set random IDs, or anything else to delete existing ones.
 * @returns {Object} The processed query instance.
 */
export function resetNodeIds(q, mode) {
  let query = cloneQuery(q);
  const random = mode === 'random';
  if (random) {
    query.id = query.id || getRandomDecimal();
  } else {
    delete query.id;
  }
  if (query.subConditionExpressions) {
    query.subConditionExpressions = query.subConditionExpressions.map(rule => {
        if (random) {
          rule.id = rule.id || getRandomDecimal();
        } else {
          delete rule.id;
        }
        if (rule.subConditionExpressions) {
          rule = resetNodeIds(rule, mode);
        }
        return rule;
    });
  }
  return query;
}

/** Deep clones a query.
 * @param {Object} query The query to be cloned.
 * @returns {Object} Another instance of the given query.
 */
export function cloneQuery(query) {
  return JSON.parse(JSON.stringify(query));
}

/** Formats a query by deleting IDs from all nodes.
 * @param {Object} query The query to be formatted.
 * @returns {Object} Another instance of the given query, without IDs.
 */
export function formatQuery(query) {
  query = cloneQuery(query);
  query = resetNodeIds(query);
  return query;
}

export function isRuleValid(rule) {
  if (!rule.conditionAttribute || !rule.condition) {
    return false;
  }
  if (/null/gi.test(rule.condition) || rule.condition === 'IS_EMPTY') {
    return true;
  }
  const { conditionValue, errorMessage, condition } = rule;

  if (Array.isArray(conditionValue) && Array.isArray(errorMessage)) {
    const hasEmptyOrWhitespaceValues = conditionValue.some(value => 
      value === '' || (typeof value === 'string' && value.trim() === '')
    );
    
    return (
      conditionValue?.length > 0 &&
      !hasEmptyOrWhitespaceValues &&
      errorMessage?.length > 0 &&
      errorMessage.includes('')
    );
  }
  if (typeof conditionValue === 'string') {
    return conditionValue.trim() !== '';
  }
  return conditionValue !== null && conditionValue !== undefined;
}

function isNotValidGroupConditions(isFirst, subConditionExpression) {
  return ((isFirst && subConditionExpression.length === 0) || (isFirst && subConditionExpression.length === 1 && subConditionExpression[0].conditionOperator) || !isFirst);
}
export function isGroupValid(group, props, isFirst) {
  if (
    Object.getOwnPropertyNames(group).filter(
      property => property !== 'communicationTemplateUuid' && property !== 'id'
    ).length === 0
  ) {
    return false;
  }

  if (
    (props.required ||
      (group?.subConditionExpressions &&
        group.subConditionExpressions.length > 0)) &&
    (!group?.subConditionExpressions ||
      group['subConditionExpressions'].length < 2)
  ) {
    if ((isNotValidGroupConditions(isFirst, group['subConditionExpressions'])))
      return false;
  }
  for (const rule of group.subConditionExpressions) {
    if (rule.subConditionExpressions) {
      if (!isGroupValid(rule, { ...props, required: true }, false)) {
        return false;
      }
    } else if (!isRuleValid(rule)) {
      return false;
    } else if (Array.isArray(rule.errorMessage)) {
      return rule.errorMessage?.length > 0 && rule.errorMessage.every(msg => msg.length === 0);  
    }
  }
  return true;
}

const emptyRule = function() {
  return {
    conditionAttribute: '',
    condition: '',
    conditionValue: [],
    dateComparisonOptions: [],
    id: getRandomDecimal(),
    height: 0,
    depth: 0
  };
};

const emptyGroup = function() {
  return {
    conditionOperator: 'OR',
    id: getRandomDecimal(),
    subConditionExpressions: [],
    height: 0,
    depth: 0
  };
};

const today = new Date().toISOString().split('T')[0];

function reducer(state, action) {
  const query = JSON.parse(JSON.stringify(state));

  switch (action.type) {
    case 'addGroup': {
      let group = findNodeById(action.id, query);
      let tempGroup = emptyGroup();
      group.subConditionExpressions.push({
        ...tempGroup,
        depth: action.level
      });
      return query;
    }
    case 'addRule': {
      let group = findNodeById(action.id, query);
      let tempRule = emptyRule();
      group.subConditionExpressions.unshift({
        ...tempRule,
        depth: action.level
      });
      return query;
    }
    case 'removeNode': {
      const parent = findParentById(action.id, query);
      parent.subConditionExpressions = parent.subConditionExpressions.filter(
        rule => rule.id !== action.id
      );
      return query;
    }
    case 'addConditionQuery': {
      let group = findNodeById(action.id, query);
      group.subConditionExpressions.push({
        queryId: action.queryId,
        depth: action.level,
        type: 'conditionQuery'
      });
      return query;
    }
    case 'removeConditionQuery': {
      let group = findNodeById(action.id, query);
      group.subConditionExpressions = group.subConditionExpressions.filter(query => query.queryId !== action.queryId);
      return query;
    }
    case 'addQuery': {
      let group = findNodeById(action.id, query);
      if (group.subConditionExpressions.length === 1) {
        group.conditionOperator = action.queryData.conditionOperator;
        group.subConditionExpressions = action.queryData.subConditionExpressions;
      } else {
        if (action.queryData.subConditionExpressions.length === 1) {
          group.subConditionExpressions.unshift({
            ...action.queryData.subConditionExpressions[0],
            id: shortid.generate(),
            depth: action.level
          });
        } else {
          group.subConditionExpressions.push({
            ...action.queryData,
            id: shortid.generate(),
            depth: action.level
          });
        }
        group.subConditionExpressions = group.subConditionExpressions.filter(query => query.queryId !== action.queryId);
      }
      return query;
    }
    case 'addRemovedNode': {
      let parent = null;
      if (action.findNode) {
        parent = findNodeById(action.id, query);
      } else {
        parent = findParentById(action.id, query);
      }
      if (parent) {
        if (
          action.position >= parent.subConditionExpressions.length ||
          action.position < 0
        ) {
          parent?.subConditionExpressions.push({
            ...action.rule,
            depth: action.level
          });
        } else {
          parent.subConditionExpressions.splice(action.position, 0, {
            ...action.rule,
            depth: action.level
          });
        }
      }
      return query;
    }
    case 'resetQuery': {
      let { query } = action;
      query = resetNodeIds(query, 'random');
      return { ...query, valid: false };
    }
    case 'setConditionOperator': {
      const node = findNodeById(action.id, query);
      node.conditionOperator = action.value;
      return query;
    }
    case 'setDataFrameConditionAttribute': {
      const node = findNodeById(action.id, query);
      node.schemaType = action.schemaType;
      node.dataFrameAttribute = action.dataFrameAttribute
      node.dataFrameConditionData = {
        dataFrameAttribute: action.value,
        dataFrameSizeQuery: false
      };
      node.dataFrameConditionQuery = true;
      node.conditionAttribute = '';
      node.condition = '';
      node.conditionValue = [''];
      node.errorMessage = [''];
      node.value = null;
      return query;
    }
    case 'setConditionAttribute': {
      const node = findNodeById(action.id, query);
      node.schemaType = action.schemaType;
      node.dataFrameAttribute = {};
      node.dataFrameConditionData= {
        dataFrameAttribute: '',
        dataFrameSizeQuery: false
      }
      node.dataFrameConditionQuery = false;
      node.conditionAttribute = action.value;
      node.condition = '';
      node.conditionValue = [''];
      node.errorMessage = [''];
      node.conditionDataType = action.conditionDataType;
      node.value = null;
      return query;
    }
    case 'setDataFrameFieldAttribute': {
      const node = findNodeById(action.id, query);
      node.dataFrameConditionData = {
        ...node.dataFrameConditionData,
        dataFrameSizeQuery: action.value === "dataframeCount" ? true : false
      }
      node.conditionAttribute = action.value;
      node.condition = '';
      node.conditionValue = [''];
      node.errorMessage = [''];
      node.conditionDataType = action.value === "dataframeCount" ? 'Integer' : action.conditionDataType;
      node.value = null;
      return query;
    }
    case 'setCondition': {
      const node = findNodeById(action.id, query);
      node.condition = action.value;
      if (node.condition === 'RANGE') {
        node.conditionValue = ['', ''];
        node.errorMessage = ['', ''];
        if (node.conditionDataType === 'Date' || node.conditionDataType === 'DateTime') {
          node.conditionValue = [today, today];
          node.dateComparisonOptions = [{
            dynamicField: null,
            dateOffset: 0,
            staticDateField: today,
            dynamicDate: false,
            dateFormatType: "DayMonthYear"
          },{
            dynamicField: null,
            dateOffset: 0,
            staticDateField: today,
            dynamicDate: false,
            dateFormatType: "DayMonthYear"
          }]
        }
      } else {
        node.conditionValue = [''];
        node.errorMessage = [''];
        if (node.conditionDataType === 'Date' || node.conditionDataType === 'DateTime') {
          node.conditionValue = [today];
          node.dateComparisonOptions = [{
            dynamicField: null,
            dateOffset: 0,
            staticDateField: today,
            dynamicDate: false,
            dateFormatType: "DayMonthYear"
          }]
        }
      }
      if (node.condition === 'IS_EMPTY') {
        node.dateComparisonOptions = [{
          dynamicField: null,
          dateOffset: 0,
          staticDateField: today,
          dynamicDate: false,
          dateFormatType: "DayMonthYear"
        }]
      }
      if (/null/.test(action.value)) {
        node.value = null;
      }
      return query;
    }
    case 'setConditionValue': {
      const node = findNodeById(action.id, query);
      node.conditionValue = action.value;
      node.dateComparisonOptions = action.dateComparisonOptions;
      node.errorMessage = action.errorMessage;
      return query;
    }
    default: {
      return query;
    }
  }
}
const findHeightOfNode = (query, h) => {
  query = cloneQuery(query);
  if (query?.conditionOperator) {
    h = h + 1;
    query['subConditionExpressions'].map(c => {
      h = findHeightOfNode(c, h);
      query.height = h - query.depth;
    });
  }
  return h;
};
function updateTemplateObject(name, props) {
  let temp = {};
  if (name === 'templates') {
    temp = {
      communicationTemplateUuid: props.communicationTemplateUuid,
      conditionOperator: 'OR',
      id: props.id,
      subConditionExpressions: [],
      valid: false
    };
  } else {
    temp = {
      conditionOperator: 'OR',
      id: getRandomDecimal(),
      subConditionExpressions: [],
      valid: false
    };
  }
  return temp;
}

function onPropsChange(props, state) {
  if (props.onChange) {
    let valid = isGroupValid(state, props, true);
    let tempQuery = { ...state, valid: valid, depth: 0 };
    let totalHeight = 1;

    if (tempQuery?.subConditionExpressions) {
      tempQuery['subConditionExpressions'].map(q => {
        totalHeight = findHeightOfNode(q, totalHeight);
        tempQuery.height = totalHeight;
      });

      props.onChange({
        target: { name: props.name, value: tempQuery }
      });
    }
  }
}
function isValidQuery(name, query) {
  return (name !== 'templates' && Object.keys(query).length === 0) ||
    (name === 'templates' &&
      !Array.isArray(query) &&
      Object.keys(query).length === 2);
}
const QueryBuilder = React.memo(props => {
  const { maxLevels, template, query, isLoading, isReadOnly, type } = props;
  const [state, dispatch] = React.useReducer(reducer, { ...query } || {});
  const [context, setContext] = React.useState(null);
  const [flattenedEventMetaData, setFlattenedEventMetaData] = useState([]);

  useEffect(() => {
    let eventFields = [];
    template.thisTemplate?.eventMetaData.map(data =>
      eventFields.push(...data.schemaConditionAttributes.filter(f => !f.metaAttribute))
    );
    setFlattenedEventMetaData(eventFields);
  }, [template.thisTemplate?.eventMetaData]);

  // Generate the context only once, or when the properties change.
  React.useEffect(() => {
    setContext({
      dispatch,
      maxLevels
    });
  }, [dispatch, props, props.maxLevels]);

  // Reset the query if it was changed externally.
  React.useEffect(() => {
    let name = props?.name?.split('.')[0];
    if (isValidQuery(name, props.query)) {
      let temp = updateTemplateObject(name, props.query);
      props.onChange({
        target: {
          name: props.name,
          value: temp
        }
      });
      dispatch({ type: 'resetQuery', query: temp });
    } else if (!props.query?.id) {
      dispatch({ type: 'resetQuery', query: { ...props.query } });
    }
  }, [props.query]);


  const readOnly = () => {
    if (isReadOnly && typeof isReadOnly === 'function') {
      return isReadOnly();
    } else {
      return false;
    }
  };
  // Propagate the change if the query is modified.
  React.useEffect(() => {
    onPropsChange(props, state);
  }, [state]);

  function appendParentIds(data) {
    let parentIds = [];
    function traverse(obj) {
      if (obj.conditionOperator) {
        obj.parentIds = parentIds.slice();
        parentIds.push(obj.id);
        if (obj.subConditionExpressions) {
        obj.subConditionExpressions = obj.subConditionExpressions.map((subObj) => {  
          return traverse({...subObj});  
        });  
        }
        parentIds.pop();
      }
      return obj;
    }
    let modifiedObject = data.map((obj) => {
    return traverse({...obj});  
    });
    return modifiedObject;
  }
  if ((state.communicationTemplateUuid !== '')) {
    return !isLoading && state.id && context ? (
      <RuleGroup
        context={context}
        template={{ ...template, attributes: flattenedEventMetaData }}
        conditionOperator={state.conditionOperator}
        id={state.id}
        level={0}
        subConditionExpressions={appendParentIds(state.subConditionExpressions)}
        isReadOnly={readOnly}
        type={type}
              />
    ) : (
      <Skeleton
        style={{ marginLeft: '8px' }}
        variant="rectangular"
        width={300}
        height={20}
      />
    );
  } else {
    return <span />;
  }
});

QueryBuilder.formatQuery = formatQuery;

QueryBuilder.defaultProps = {
  maxLevels: 10,
  name: 'conditionExpression',
  required: false,
  template: {},
  onChange: null,
  query: {
    conditionOperator: 'OR',
    id: getRandomDecimal(),
    subConditionExpressions: [],
    valid: false
  }
};

export default QueryBuilder;
